import { Link } from "react-router-dom";
import Reveal from "@/components/base/Reveal";
import { journeyTimeline } from "@/mocks/journeys";

export default function JourneyTimeline() {
  let lastSeason = "";

  return (
    <section
      id="timeline"
      className="relative overflow-hidden bg-foreground-950 text-background-50 grain scroll-mt-20"
    >
      {/* Map-inspired dotted field */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.08]"
        style={{
          backgroundImage:
            "radial-gradient(oklch(var(--background-50)) 1px, transparent 1px)",
          backgroundSize: "28px 28px",
        }}
        aria-hidden="true"
      />

      <div className="relative z-10 w-full px-5 md:px-10 lg:px-16 py-20 md:py-28">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <Reveal>
              <p className="eyebrow text-primary-400">The 2026–27 Season</p>
            </Reveal>
            <Reveal delay={120}>
              <h2 className="mt-5 font-heading text-3xl leading-[1.05] sm:text-4xl md:text-5xl">
                Where Eddie &amp; Kiki are{" "}
                <span className="italic text-primary-400">heading next.</span>
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 lg:self-end">
            <Reveal delay={200}>
              <p className="font-body text-base leading-relaxed text-background-50/70">
                A living travel journal, not a calendar. Transcribed from the
                handwritten schedule — every stop a place, a date and a story
                waiting to be told. Entries marked{" "}
                <span className="text-background-50/90">to be confirmed</span>{" "}
                are being filled in.
              </p>
            </Reveal>
            <Reveal delay={260}>
              <div className="mt-7 flex flex-wrap items-center gap-x-8 gap-y-3 font-label text-[10px] uppercase tracking-label text-background-50/50">
                <span className="inline-flex items-center gap-2">
                  <i className="ri-map-pin-2-line text-primary-400" />
                  October 2026 — March 2027
                </span>
                <span className="inline-flex items-center gap-2">
                  <i className="ri-route-line text-primary-400" />
                  {journeyTimeline.length} stops
                </span>
              </div>
            </Reveal>
          </div>
        </div>

        <ol className="mt-16">
          {journeyTimeline.map((stop, i) => {
            const showSeason = stop.season !== lastSeason;
            lastSeason = stop.season;
            const isTbc = stop.status === "tbc";

            return (
              <li key={stop.slug}>
                {showSeason && (
                  <Reveal>
                    <div className="flex items-center gap-5 pt-10 pb-6">
                      <span className="font-label text-[11px] uppercase tracking-label text-primary-400">
                        {stop.season}
                      </span>
                      <span className="h-px flex-1 bg-background-50/15" />
                      <i className="ri-compass-3-line text-lg text-background-50/30" />
                    </div>
                  </Reveal>
                )}

                <Reveal delay={Math.min(i % 4, 3) * 70}>
                  <div className="group grid grid-cols-1 gap-4 md:grid-cols-12 md:gap-8">
                    <div className="md:col-span-3">
                      <div className="font-heading text-3xl tabular-nums leading-none text-background-50 md:text-4xl">
                        {stop.dateShort}
                      </div>
                      <div className="mt-2 font-label text-[10px] uppercase tracking-label text-background-50/45">
                        {stop.monthLabel}
                      </div>
                    </div>

                    <div className="relative md:col-span-9 md:border-l md:border-background-50/15 md:pl-10 md:pb-12">
                      <span
                        className={`absolute left-0 top-2 hidden h-[9px] w-[9px] -translate-x-1/2 rounded-full transition-colors duration-500 md:block ${
                          isTbc
                            ? "border border-background-50/40 bg-foreground-950"
                            : "bg-primary-400 group-hover:bg-primary-300"
                        }`}
                        aria-hidden="true"
                      />

                      <Link
                        to={`/culinary-journeys/${stop.slug}`}
                        className="block"
                      >
                        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-2">
                          <h3
                            className={`font-heading text-2xl leading-tight transition-colors duration-500 group-hover:text-primary-300 md:text-4xl ${
                              isTbc ? "italic text-background-50/70" : ""
                            }`}
                          >
                            {stop.destination}
                          </h3>
                          {isTbc && (
                            <span className="rounded-full border border-dashed border-background-50/35 px-3 py-1 font-label text-[9px] uppercase tracking-label text-background-50/60">
                              Detail to be confirmed
                            </span>
                          )}
                        </div>

                        {stop.note && (
                          <p className="mt-2 font-body text-sm text-background-50/60">
                            {stop.note}
                          </p>
                        )}
                        {stop.region && !stop.note && (
                          <p className="mt-2 font-label text-[10px] uppercase tracking-label text-background-50/45">
                            {stop.region}
                          </p>
                        )}

                        <span className="mt-4 inline-flex items-center gap-2 font-label text-[10px] uppercase tracking-label text-background-50/50 transition-colors duration-500 group-hover:text-primary-400">
                          Open this stop
                          <i className="ri-arrow-right-line text-sm transition-transform duration-500 group-hover:translate-x-1" />
                        </span>
                      </Link>

                      {stop.image && (
                        <Reveal variant="mask" delay={120}>
                          <Link
                            to={`/culinary-journeys/${stop.slug}`}
                            className="mt-6 block overflow-hidden image-zoom"
                            aria-label={`Open journey: ${stop.destination}`}
                          >
                            <div className="aspect-[16/9] w-full overflow-hidden bg-foreground-900 md:aspect-[21/9]">
                              <img
                                src={stop.image}
                                alt={`${stop.destination} — Chef Eddie & Kiki G culinary journey`}
                                title={`${stop.destination} — Chef Eddie & Kiki G`}
                                className="h-full w-full object-cover object-center"
                              />
                            </div>
                          </Link>
                        </Reveal>
                      )}
                    </div>
                  </div>
                </Reveal>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}